import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreditCardIcon,
  ArrowPathIcon,
  UserPlusIcon,
  UserMinusIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { billingApi } from '../../utils/api';

/**
 * Live billing panel for the Settings page. Replaces the previous static
 * mock and talks to `/billing/*`:
 *  - current plan + seat count
 *  - seat adjuster (Company only, prorated via Stripe)
 *  - link into the Stripe Customer Portal to manage cards / invoices
 *  - cancel-at-period-end toggle
 */
export default function BillingPanel() {
  const qc = useQueryClient();
  const { data: sub, isLoading, error } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => billingApi.getSubscription().then((r) => r.data),
  });

  const [seats, setSeats] = useState<number>(sub?.seatCount || 2);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    if (sub?.seatCount && !busy) setSeats(sub.seatCount);
  }, [sub?.seatCount, busy]);

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
        <ArrowPathIcon className="w-4 h-4 animate-spin" /> Loading billing…
      </div>
    );
  }

  if (error || !sub) {
    return (
      <div className="p-6 text-sm text-slate-500">
        No active subscription found. If you just signed up, try refreshing in a moment.
      </div>
    );
  }

  const isCompany = sub.plan === 'COMPANY';
  const unitPence = sub.unitAmountPence || (isCompany ? 4000 : 2000);
  const monthly = ((unitPence * (sub.seatCount || 1)) / 100).toFixed(2);
  const periodEnd = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : null;
  const status = String(sub.status || '').toLowerCase();

  const updateSeats = async (next: number) => {
    setNotice(null);
    setErrMsg(null);
    setBusy('seats');
    try {
      await billingApi.updateSeats(next);
      qc.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      setNotice(`Seats updated to ${next}.`);
    } catch (err: any) {
      setErrMsg(err?.response?.data?.message || 'Could not update seats.');
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setErrMsg(null);
    setBusy('portal');
    try {
      const resp = await billingApi.openPortal();
      const url = resp.data?.url;
      if (url) window.location.assign(url);
    } catch (err: any) {
      setErrMsg(err?.response?.data?.message || 'Could not open billing portal.');
    } finally {
      setBusy(null);
    }
  };

  const toggleCancel = async () => {
    setErrMsg(null);
    setBusy('cancel');
    try {
      await billingApi.cancel(!sub.cancelAtPeriodEnd);
      qc.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    } catch (err: any) {
      setErrMsg(err?.response?.data?.message || 'Could not update cancellation.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Current plan */}
      <div className="rounded-xl border border-[#E6E6E4] bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-[#181818]">
              <CreditCardIcon className="w-4 h-4" />
              Current plan
            </div>
            <div className="mt-3 text-lg font-semibold text-slate-900">
              {isCompany ? 'Company' : 'Solo engineer'} ·{' '}
              <span className="text-slate-500 text-sm font-normal">
                £{monthly}/month
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {isCompany
                ? `${sub.seatCount} seats × £${(unitPence / 100).toFixed(0)}`
                : '1 user'}
              {periodEnd ? ` · Renews ${periodEnd}` : ''}
            </div>
            <StatusPill status={status} cancel={sub.cancelAtPeriodEnd} />
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={busy === 'portal'}
              onClick={openPortal}
              className="inline-flex items-center gap-2 rounded-lg border border-[#E6E6E4] bg-white px-3 py-2 text-xs text-[#181818] hover:bg-slate-50"
            >
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              Manage in Stripe
            </button>
            <button
              type="button"
              disabled={busy === 'cancel'}
              onClick={toggleCancel}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                sub.cancelAtPeriodEnd
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              {sub.cancelAtPeriodEnd ? 'Resume subscription' : 'Cancel at period end'}
            </button>
          </div>
        </div>

        {notice && (
          <div className="mt-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 inline-flex items-center gap-2">
            <CheckCircleIcon className="w-3.5 h-3.5" /> {notice}
          </div>
        )}
        {errMsg && (
          <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {errMsg}
          </div>
        )}
      </div>

      {/* Seats (Company only) */}
      {isCompany && (
        <div className="rounded-xl border border-[#E6E6E4] bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm font-medium text-[#181818]">Team seats</div>
              <div className="text-xs text-slate-500 mt-1 max-w-md">
                Each seat is £{(unitPence / 100).toFixed(0)}/month. Changes are prorated and
                billed automatically on your next invoice.
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSeats(Math.max(2, seats - 1))}
                disabled={seats <= 2 || busy === 'seats'}
                className="h-9 w-9 rounded-lg border border-[#E6E6E4] flex items-center justify-center text-slate-600 disabled:opacity-40"
              >
                <UserMinusIcon className="w-4 h-4" />
              </button>
              <input
                type="number"
                min={2}
                className="w-16 text-center rounded-lg border border-[#E6E6E4] py-2 text-sm font-medium"
                value={seats}
                onChange={(e) => setSeats(Math.max(2, Number(e.target.value) || 2))}
              />
              <button
                type="button"
                onClick={() => setSeats(seats + 1)}
                disabled={busy === 'seats'}
                className="h-9 w-9 rounded-lg border border-[#E6E6E4] flex items-center justify-center text-slate-600"
              >
                <UserPlusIcon className="w-4 h-4" />
              </button>

              <button
                type="button"
                disabled={busy === 'seats' || seats === sub.seatCount}
                onClick={() => updateSeats(seats)}
                className="ml-3 inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-3 py-2 text-xs font-semibold disabled:opacity-50"
              >
                {busy === 'seats' ? 'Saving…' : 'Save seats'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isCompany && (
        <div className="rounded-xl border border-[#E6E6E4] bg-white p-5 shadow-sm text-sm text-slate-600">
          Need to add engineers? Upgrade to the <strong>Company</strong> plan in the Stripe portal
          above — you'll get team seats, an engineer portal, and a price book with approvals.
        </div>
      )}
    </div>
  );
}

const StatusPill: React.FC<{ status: string; cancel?: boolean }> = ({ status, cancel }) => {
  const tone =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'trial'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-slate-100 text-slate-600 border-slate-200';
  const label = cancel
    ? 'Cancels at period end'
    : status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : 'Unknown';
  return (
    <span className={`mt-3 inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${tone}`}>
      {label}
    </span>
  );
};
